import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconSearch } from '@tabler/icons-react';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';

interface GlassNavSearchProps {
  isVisible: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const SearchIcon = () => (
  <Box className="flex items-center justify-center">
    <IconSearch size={18} stroke={2} className="text-od-content-tertiary" aria-hidden="true" />
  </Box>
);

const DesktopNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible }) => (
  <Box
    className={cn(
      'flex items-center transition-[opacity,max-width] duration-300',
      isVisible
        ? 'pointer-events-auto max-w-120 opacity-100'
        : 'pointer-events-none max-w-0 opacity-0',
    )}
  >
    <Box className="w-full min-w-0 flex-1">
      <NewSellerFlowAddressInput
        hideLabel
        inputLocation="hero-cta"
        analyticsPrefix="cosmos-glass-nav-search-desktop"
        trackingTaxonomy="new_homepage"
        placeholder="Enter home address"
        icon={<SearchIcon />}
        size="xsmall"
      />
    </Box>
  </Box>
);

const MobileNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible, onExpandChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapseRafRef = useRef<number>(0);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const expand = useCallback(() => {
    if (typeof window !== 'undefined') {
      cancelAnimationFrame(collapseRafRef.current);
    }
    setIsExpanded(true);
    onExpandChange?.(true);
  }, [onExpandChange]);

  const collapseIfBlurred = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    collapseRafRef.current = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (
        containerRef.current &&
        !containerRef.current.contains(active) &&
        !closeButtonRef.current?.contains(active)
      ) {
        setIsExpanded(false);
        onExpandChange?.(false);
      }
    });
  }, [onExpandChange]);

  const collapseExplicitly = useCallback(() => {
    if (typeof window !== 'undefined') {
      cancelAnimationFrame(collapseRafRef.current);
    }
    setIsExpanded(false);
    onExpandChange?.(false);
  }, [onExpandChange]);

  useEffect(() => {
    return () => {
      if (collapseRafRef.current) {
        cancelAnimationFrame(collapseRafRef.current);
      }
    };
  }, []);

  return (
    <Row
      className={cn(
        'w-full items-center gap-2 transition-opacity duration-300',
        isVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <Box
        ref={closeButtonRef}
        onClick={collapseExplicitly}
        aria-label="Close search"
        aria-hidden={!isExpanded}
        tabIndex={isExpanded ? 0 : -1}
        analyticsName="cosmos-glass-nav-search-close"
        className={cn(
          'flex h-11 shrink-0 cursor-pointer items-center justify-center overflow-hidden border-0 bg-transparent p-0 transition-[width,opacity] duration-200',
          isExpanded ? 'w-7 opacity-100' : 'w-0 opacity-0',
        )}
      >
        <IconChevronLeft size={22} className="text-od-content-primary" aria-hidden="true" />
      </Box>

      <Box
        ref={containerRef}
        onFocus={expand}
        onBlur={collapseIfBlurred}
        className="min-w-0 flex-1"
      >
        <NewSellerFlowAddressInput
          hideLabel
          inputLocation="hero-cta"
          analyticsPrefix="cosmos-glass-nav-search-mobile"
          trackingTaxonomy="new_homepage"
          placeholder="Enter home address"
          icon={<SearchIcon />}
          size="xsmall"
        />
      </Box>
    </Row>
  );
};

export const GlassNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible, onExpandChange }) => {
  if (onExpandChange) {
    return <MobileNavSearch isVisible={isVisible} onExpandChange={onExpandChange} />;
  }
  return <DesktopNavSearch isVisible={isVisible} />;
};
